import { Form } from "@inertiajs/react"
import { BellRing } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { remindShipmentPath } from "@/routes"

interface RemindCreatorButtonProps {
  collabId: number
  /** ISO timestamp when the next reminder is allowed, or null if allowed now. */
  reminderAvailableAt: string | null
  creatorLabel: string
  /** OKS-1040 — the row waits on the PRODUCT choice: product-specific aria. */
  productChoice?: boolean
}

/**
 * OKS-984 — "Remind creator" on a Waiting row (UX Spec §2.1).
 *
 * Inertia `<Form>` (Crew rule) — no shadcn Form, no react-hook-form, no zod.
 * `remindShipmentPath` from js-routes; never a hardcoded URL.
 *
 * # ⚠️ The disabled state is an AFFORDANCE, not the rate limit
 *
 * `reminderAvailableAt` comes from the server and only decides what this
 * button looks like. The 24h limit is enforced by a predicated `update_all` in
 * `Collabs::RemindAddress`, which is the serialisation point — two
 * simultaneous clicks produce one notification regardless of what this
 * component renders. A reader who treated this disabled state as the guard
 * would have written the double-nudge.
 *
 * That separation is why a server-side refusal still has to surface: it
 * arrives as `errors.base` and the row toasts it. If the button were the
 * guard, the refusal path would be unreachable and untested.
 */
export function RemindCreatorButton({
  collabId,
  reminderAvailableAt,
  creatorLabel,
  productChoice = false,
}: RemindCreatorButtonProps) {
  const { t } = useTranslation()
  const waiting = Boolean(reminderAvailableAt)

  return (
    <Form
      action={remindShipmentPath(collabId)}
      method="post"
      data-testid="shipments-row-remind-form"
    >
      {({ processing }) => (
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={waiting || processing}
          aria-label={
            productChoice
              ? t("shipments.remind.product_aria", { creator: creatorLabel })
              : t("shipments.remind.aria", { creator: creatorLabel })
          }
          data-testid="shipments-row-remind-trigger"
        >
          <BellRing aria-hidden="true" />
          {waiting
            ? t("shipments.remind.cooling_down")
            : t("shipments.remind.label")}
        </Button>
      )}
    </Form>
  )
}
